import React, {
  FC, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { DateRange } from '@mui/x-date-pickers-pro';
import { Dayjs } from 'dayjs';
import DeleteIcon from '../../../assets/icons/delete-icon.svg';
import SaveIcon from '../../../assets/icons/save-icon.svg';
import PlusIcon from '../../../assets/icons/plus-icon.svg';
import { useGetDashboards } from '../../../apiHooks/queries/useGetDashboards';
import BasicDialog from '../../../dialogs/basicDialog';
import DashboardsForm from '../../../forms/dashboardsForm';
import { useDeleteDashboard } from '../../../apiHooks/mutations/dashboardMutations/dashboardMutations';
import AddDashboardElementForm from '../../../forms/addDashboardElementForm';
import { dashboardElementOptionsLength } from '../../../constants/dashboardConstants';
import SecondaryButton from '../../buttons/secondaryButton';
import DashboardSubMenu from '../../subMenus/dashboardSubMenu/dashboardSubMenu';
import DashboardSelect from '../../dropdownSelects/dashboardSelect';
import CustomDateRangePicker from '../../../partials/customDateRangePicker/customDateRangePicker';
import CalendarIconPrimary from '../../../assets/icons/calendar-icon-primary.svg';
import { CustomButton } from '../../buttons/customButton';
import { Colors } from '../../../constants/constants';

interface DashboardActionsProps {
  selectedOption: number | undefined,
  setSelectedOption: (_newSelectedOption: number) => void,
  refetchSelectedDashboardData: () => void,
  updateLayout: (_newLayout: any) => void,
  isUpdatingLayout: boolean,
  setNewLayout: (_newLayout: any) => void,
  newLayout: any,
  isDefaultDashboard: boolean,
  selectedDashboardData?: any,
  filterRange: DateRange<Dayjs>;
  setFilterRange: (_range: DateRange<Dayjs>) => void;
}

const DashboardActions: FC<DashboardActionsProps> = (props) => {
  const [isDashboardDialogOpen, setIsDashboardDialogOpen] = useState<boolean>(false);
  const [isNewDashboardDialog, setIsNewDashboardDialog] = useState<boolean>(false);
  const [searchedValue, setSearchedValue] = useState<string>('');
  const [isCalendarOpen, setIsCalendarOpen] = useState<boolean>(false);

  const { mutateAsync: deleteDashboard } = useDeleteDashboard();
  const { t } = useTranslation();

  const selectedDashboardLength = props.selectedDashboardData
    && JSON.parse(props.selectedDashboardData?.data?.layout).length;

  const {
    data: dashboardOptions,
    isError: isErrorDashboardOptions,
    isRefetching: isRefetchingDashboardOptions,
    refetch: refetchDashboardOptions,
  } = useGetDashboards('layoutOptions', searchedValue);

  const closeDialogAndRefetch = async () => {
    setIsDashboardDialogOpen(false);
    props.refetchSelectedDashboardData();
    await refetchDashboardOptions();
  };

  const saveLayout = async () => {
    await props.updateLayout(props.newLayout);
    props.setNewLayout(null);
  };

  const deleteLayout = async () => {
    if (props.selectedOption) await deleteDashboard(props.selectedOption);
    props.setSelectedOption(0);
    await refetchDashboardOptions();
  };

  const openNewElementDialog = () => {
    setIsNewDashboardDialog(false);
    setIsDashboardDialogOpen(true);
  };

  const openNewDashboardDialog = () => {
    setIsNewDashboardDialog(true);
    setIsDashboardDialogOpen(true);
  };

  return (
    <>
      <BasicDialog
        closeDialog={() => setIsDashboardDialogOpen(false)}
        open={isDashboardDialogOpen}
        title={isNewDashboardDialog ? t('dashboardForm') : t('addNewElementForm')}
      >
        {isNewDashboardDialog ? (
          <DashboardsForm closeDialog={closeDialogAndRefetch} />
        ) : (
          <AddDashboardElementForm
            updateLayout={props.updateLayout}
            isUpdatingLayout={props.isUpdatingLayout}
            selectedDashboard={props.selectedOption}
            closeDialog={closeDialogAndRefetch}
            selectedDashboardData={props.selectedDashboardData}
          />
        )}
      </BasicDialog>
      <div data-cy="dashboard-actions" className="flex justify-between pb-3">
        <div className="flex items-center gap-3">
          <DashboardSelect
            data={!props.isDefaultDashboard ? props.selectedDashboardData?.data : null}
            searchedValue={searchedValue}
            setSearchedValue={setSearchedValue}
            dashboardOptions={dashboardOptions}
            isErrorDashboardOptions={isErrorDashboardOptions}
            isRefetchingDashboardOptions={isRefetchingDashboardOptions}
            refetchDashboardOptions={refetchDashboardOptions}
            setSelectedOption={props.setSelectedOption}
            size="small"
          />
        </div>
        <div className="flex items-center gap-0.5">
          {!props.isDefaultDashboard && (
            <>
              <SecondaryButton
                type="button"
                style={{ paddingInline: '1rem' }}
                disabled={!props.newLayout || props.isUpdatingLayout}
                onClick={saveLayout}
                dataCy="dashboard-actions-save-dashboard-layout"
              >
                <img src={SaveIcon} alt="" className="h-[21px] w-full object-contain" />
              </SecondaryButton>
              <SecondaryButton
                type="button"
                style={{ paddingInline: '1rem' }}
                onClick={deleteLayout}
                dataCy="dashboard-actions-delete-dashboard"
              >
                <img src={DeleteIcon} alt="" className="h-[21px] w-full object-contain" />
              </SecondaryButton>
            </>
          )}
          <SecondaryButton
            type="button"
            style={{
              paddingInline: '1rem',
              height: '40px',
              marginLeft: '1rem',
              paddingBlock: 0,
              backgroundColor: 'white',
              border: `1px solid ${isCalendarOpen ? Colors.primary : '#eaeaea'}`,
            }}
            onMouseDown={() => setIsCalendarOpen(true)}
            dataCy="dashboard-actions-calendar-toggle"
          >
            <CustomDateRangePicker
              filterRange={props.filterRange}
              setFilterRange={props.setFilterRange}
              isCalendarOpen={isCalendarOpen}
              setIsCalendarOpen={setIsCalendarOpen}
              iconButton
              showFilterDates
            />
          </SecondaryButton>
          {(!props.isDefaultDashboard && props.selectedDashboardData
            && selectedDashboardLength !== dashboardElementOptionsLength) ? (
              <div className="relative z-20">
                <DashboardSubMenu
                  openNewElementDialog={openNewElementDialog}
                  openNewDashboardDialog={openNewDashboardDialog}
                />
              </div>
            ) : (
              <CustomButton
                type="button"
                onClick={openNewDashboardDialog}
                className="ml-6 h-[40px] py-0"
                // dataCy="dashboard-actions-open-new-dashboard-dialog"
              >
                <img src={PlusIcon} alt="" />
                {t('new')}
              </CustomButton>
            )}
        </div>
      </div>
    </>
  );
};

export default DashboardActions;
